import { useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASONS = ["Upgrade", "Renewal", "Lost", "Blocked", "Fraud", "Closed", "Bank Replacement", "Other"];
const STATUSES = ["Active", "Inactive", "Closed", "Blocked", "Lost", "Replaced"];

const EMPTY = {
    old_card_id: "",
    new_card_id: "",
    reason: "Upgrade",
    request_date: new Date().toISOString().slice(0, 10),
    effective_date: "",
    bank_reference: "",
    old_card_status: "Replaced",
    new_card_status: "Active",
    remarks: "",
};

export default function CardHistory() {
    const { hasRole } = useAuth();
    const [cards, setCards] = useState([]);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [f, setF] = useState(EMPTY);

    const load = () => api.get("/card-history").then((r) => setRows(r.data));
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
        load();
    }, []);

    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...f };
            if (!payload.old_card_id) delete payload.old_card_id;
            if (!payload.new_card_id) delete payload.new_card_id;
            const { data } = await api.post("/card-history", payload);
            toast.success("History entry created");
            setOpen(false);
            setF(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const uploadDoc = async (hid, kind, file) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        try {
            await api.post(`/card-history/${hid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document uploaded");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));
    const label = (id, fallback4) => {
        const c = cardMap[id];
        if (c) return `${c.bank_name} · ${c.card_name} · •••• ${c.last_4}`;
        return fallback4 ? `•••• ${fallback4}` : "—";
    };

    return (
        <div data-testid="card-history-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Card History</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Closures &amp; replacements.</h1>
                    <p className="text-sm text-stone-500 mt-3">{rows.length} history entries · preserves links to old dues, refunds and disputes.</p>
                </div>
                {hasRole("admin", "finance") && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-history-btn"><Plus size={16} className="mr-1.5" /> Record change</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
                            <DialogHeader><DialogTitle className="font-display text-2xl">Card lifecycle event</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Reason</Label>
                                        <Select value={f.reason} onValueChange={set("reason")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Old card</Label>
                                        <Select value={f.old_card_id || "none"} onValueChange={(v) => set("old_card_id")(v === "none" ? "" : v)}>
                                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">— None —</SelectItem>
                                                {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">New card</Label>
                                        <Select value={f.new_card_id || "none"} onValueChange={(v) => set("new_card_id")(v === "none" ? "" : v)}>
                                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">— None —</SelectItem>
                                                {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="eyebrow">Request date</Label><Input type="date" value={f.request_date} onChange={setInput("request_date")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Effective date</Label><Input type="date" value={f.effective_date} onChange={setInput("effective_date")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Bank reference</Label><Input value={f.bank_reference} onChange={setInput("bank_reference")} className="mt-1.5" /></div>
                                    <div>
                                        <Label className="eyebrow">Old status → set to</Label>
                                        <Select value={f.old_card_status} onValueChange={set("old_card_status")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} className="mt-1.5" /></div>
                                <Button type="submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white">Save</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr><th>Effective</th><th>Reason</th><th>Old Card</th><th></th><th>New Card</th><th>Bank Ref</th><th>Docs</th><th>Remarks</th></tr>
                        </thead>
                        <tbody>
                            {rows.map((h) => (
                                <tr key={h.id}>
                                    <td className="tabular-nums">{fmtDate(h.effective_date || h.request_date)}</td>
                                    <td>{h.reason}</td>
                                    <td>{label(h.old_card_id, h.old_last_4)}</td>
                                    <td className="text-stone-400"><ArrowRight size={14} /></td>
                                    <td>{label(h.new_card_id, h.new_last_4)}</td>
                                    <td className="font-mono-figure text-xs">{h.bank_reference || "—"}</td>
                                    <td>
                                        <div className="flex flex-col gap-1 text-[11px]">
                                            {h.closure_confirmation_file ? (
                                                <a href={`${BACKEND_URL}${h.closure_confirmation_file}`} target="_blank" rel="noreferrer" className="underline">Closure ↗</a>
                                            ) : hasRole("admin", "finance") ? (
                                                <label className="underline text-stone-500 cursor-pointer">
                                                    <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadDoc(h.id, "closure", e.target.files[0])} />
                                                    + Closure
                                                </label>
                                            ) : null}
                                            {h.replacement_letter_file ? (
                                                <a href={`${BACKEND_URL}${h.replacement_letter_file}`} target="_blank" rel="noreferrer" className="underline">Replacement ↗</a>
                                            ) : hasRole("admin", "finance") ? (
                                                <label className="underline text-stone-500 cursor-pointer">
                                                    <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadDoc(h.id, "replacement", e.target.files[0])} />
                                                    + Replacement
                                                </label>
                                            ) : null}
                                        </div>
                                    </td>
                                    <td className="text-xs max-w-[200px] truncate">{h.remarks || "—"}</td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-stone-500 py-14">No history entries.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
